import { useEffect, useMemo, useState } from 'react'
import Highcharts from 'highcharts'
import { Button, Grid, IconButton as MuiIconButton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { ReactComponent as Up } from './icons/UpIcon.svg'
import { ReactComponent as UpDisable } from './icons/UpDisable.svg'
import { ReactComponent as Down } from './icons/DownIcon.svg'
import { ReactComponent as DownDisable } from './icons/DownDisable.svg'
import Dashboards from '@highcharts/dashboards/es-modules/masters/dashboards.src'
import '@highcharts/dashboards/es-modules/masters/modules/layout.src'
import { range } from 'lodash'

Dashboards.HighchartsPlugin.custom.connectHighcharts(Highcharts)

Dashboards.PluginHandler.addPlugin(Dashboards.HighchartsPlugin)

const IconButton = styled(MuiIconButton)({
	display: 'block',
	marginLeft: 'auto',
	marginRight: 'auto'
})

const TrendAnalysisChart = ({ options }: any) => {
	// this is config array of every chart
	const [order, setOrder] = useState(range(0, options.length))

	useEffect(() => {
		setOrder(prev => [...prev, ...range(prev.length, options.length)])
	}, [options.length])

	// for moving up the chart
	const moveUp = (index: any) => {
		if (index === 0) return
		const newItems = [...order]

		// Swap the elements
		const temp = newItems[index - 1]
		newItems[index - 1] = newItems[index]
		newItems[index] = temp

		setOrder(newItems)
	}

	// moving down the chart
	const moveDown = (index: any) => {
		if (index === order?.length - 1) return

		const newItems = [...order]

		// Swap the elements
		const temp = newItems[index + 1]
		newItems[index + 1] = newItems[index]
		newItems[index] = temp

		setOrder(newItems)
	}

	const config: any = useMemo(() => {
		return {
			dataPool: {
				connectors: [
					{
						id: 'micro-element',
						type: 'JSON',
						options: {
							firstRowAsNames: false,
							columnNames: [
								'Time',
								...options.map((option: any) => option.series[0].name)
							],
							data: options[0].xAxis.categories.map(
								(time: string, i: number) => {
									const arr = [time]

									options.forEach((option: any) => {
										arr.push(option.series[0].data[i])
									})

									return arr
								}
							)
						}
					}
				]
			},
			gui: {
				layouts: [
					{
						rows: order.map((i: number) => ({
							cells: [
								{
									id: `dashboard-col-${i}`
								}
							]
						}))
					}
				]
			},
			components: options.map((option: any, i: number) => ({
				cell: `dashboard-col-${i}`,
				type: 'Highcharts',
				sync: {
					visibility: true,
					highlight: true,
					extremes: true
				},
				connector: {
					id: 'micro-element',
					columnAssignment: [
						{
							seriesId: option.series[0].name,
							data: ['Time', option.series[0].name]
						}
					]
				},
				chartOptions: {
					...option,
					series: undefined,
					legend: {
						enabled: false
					},
					plotOptions: {
						series: {
							color: option.title.style.color
						}
					},
					xAxis: {
						...options.xAxis,
						tickInterval: 10,
						crosshair: true,
						categories: undefined,
						type: 'category',
						lineColor: '#808080',
						labels:
							i === order[order.length - 1]
								? option.xAxis.labels
								: { enabled: false },
						gridLineWidth: 0.5,
						gridLineDashStyle: 'shortdash',
						gridLineColor: '#bbbbbb',
					}
				}
			}))
		}
	}, [options, order])

	useEffect(() => {
		Dashboards.board('highchart-container', config)
	}, [config])

	return (
		<Grid style={{ background: '#353f7f' }}>
			<Grid container>
				<Grid item xs={0.5}>
					<Grid
						container
						flexDirection={'column'}
						justifyContent='space-around'
						height='100%'
					>
						{/* UP button UI */}
						{options.map((_: any, index: number) => (
							<>
								<Grid item textAlign='center'>
									{!(index === 0) ? (
										<IconButton onClick={() => moveUp(index)}>
											<Up />
										</IconButton>
									) : (
										<IconButton style={{ cursor: 'context-menu' }}>
											<UpDisable />
										</IconButton>
									)}

									{/* DOWN button UI */}

									{!(index === options?.length - 1) ? (
										<IconButton onClick={() => moveDown(index)}>
											<Down />
										</IconButton>
									) : (
										<IconButton style={{ cursor: 'context-menu' }}>
											<DownDisable />
										</IconButton>
									)}
								</Grid>
							</>
						))}
					</Grid>
				</Grid>
				<Grid item xs={11.5}>
					<div id='highchart-container' />
				</Grid>
			</Grid>
			{/* section to show legends on the bottom of graph  */}
			<Grid
				container
				style={{
					padding: '10px',
					margin: '10px',
					width: 'auto',
					display: 'flex',
					justifyContent: 'center'
				}}
			>
				{options?.map((opt: any) => (
					<Grid
						item
						style={{
							display: 'flex',
							alignItems: 'center',
							marginRight: '20px'
						}}
					>
						{/* mapping over the option series for getting  tag names*/}
						{opt?.series.map((series: any) => (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									marginRight: '10px'
								}}
							>
								<div
									style={{
										width: '12px',
										height: '5px',
										backgroundColor: series.color,
										marginRight: '5px'
									}}
								/>
								<span style={{ color: '#FFFFFF' }}>{series.name}</span>
							</div>
						))}
					</Grid>
				))}
			</Grid>
		</Grid>
	)
}

export default TrendAnalysisChart
